import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface Prediction {
  id: string;
  type: 'suggestion' | 'warning' | 'opportunity' | 'schedule';
  title: string;
  description: string;
  confidence: number;
  timeframe?: string;
  impact?: 'high' | 'medium' | 'low';
}

interface PredictionPanelProps {
  predictions: Prediction[];
  loading?: boolean;
}

const PredictionPanel: React.FC<PredictionPanelProps> = ({
  predictions,
  loading = false,
}) => {
  const theme = useTheme();

  const getIcon = (type: Prediction['type']) => {
    switch (type) {
      case 'suggestion':
        return <LightbulbIcon color="primary" />;
      case 'warning':
        return <WarningIcon color="error" />;
      case 'opportunity':
        return <TrendingUpIcon color="success" />;
      case 'schedule':
        return <ScheduleIcon color="info" />;
      default:
        return <LightbulbIcon />;
    }
  };

  const getImpactColor = (impact?: string) => {
    switch (impact) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Predictions
          </Typography>
          <LinearProgress />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            Analyzing your patterns...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (predictions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Predictions
          </Typography>
          <Alert severity="info">
            No predictions available yet. Keep using the system to enable AI insights.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          AI Predictions
        </Typography>
        <List>
          {predictions.map((prediction) => (
            <ListItem
              key={prediction.id}
              style={{
                marginBottom: 16,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 4,
              }}
            >
              <ListItemIcon>{getIcon(prediction.type)}</ListItemIcon>
              <ListItemText
                primary={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography variant="subtitle1">{prediction.title}</Typography>
                    {prediction.impact && (
                      <Chip
                        label={prediction.impact}
                        size="small"
                        color={getImpactColor(prediction.impact) as any}
                      />
                    )}
                  </div>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="textSecondary">
                      {prediction.description}
                    </Typography>
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16 }}>
                      {prediction.timeframe && (
                        <Typography variant="caption" color="textSecondary">
                          {prediction.timeframe}
                        </Typography>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Typography variant="caption" color="textSecondary">
                          Confidence:
                        </Typography>
                        <div style={{ width: 60 }}>
                          <LinearProgress
                            variant="determinate"
                            value={prediction.confidence}
                            style={{ height: 4, borderRadius: 8 }}
                          />
                        </div>
                        <Typography variant="caption" color="textSecondary">
                          {prediction.confidence}%
                        </Typography>
                      </div>
                    </div>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default PredictionPanel;